import React, { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Plus, X, ChevronDown } from 'lucide-react';
import { Input } from '../../../components/ui/input';
import { Button } from '../../../components/ui/button';
import { LoadingButton } from '../../../components/common';
import { Tabs, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../../components/ui/select';
import PreviewWidget from './PreviewWidget';
import { useUpdateDomain } from '../hooks/useDomains';
import { DomainDetail } from '../../../types';
import { RATE_LIMIT, MESSAGE_LENGTH, CONTEXT_TURNS, DEFAULT_BOT_SETTINGS, SYSTEM_PROMPT_TEMPLATES, PLAN_INFO } from '../../../config/constants';
import { useAuthStore } from '../../../stores/authStore';
import { isValidHexColor } from '../../../lib/utils';
import { useFormGuard } from '../../../hooks/useFormGuard';
import { UnsavedChangesModal } from '../../../components/common';

const settingsSchema = z.object({
  description: z.string().max(500, '説明は500文字以内で入力してください').optional(),
  bot_name: z.string().min(1, 'BOT名を入力してください').max(50, 'BOT名は50文字以内で入力してください'),
  welcome_message: z.string().max(200, 'ウェルカムメッセージは200文字以内で入力してください'),
  placeholder: z.string().max(100, 'プレースホルダーは100文字以内で入力してください'),
  primary_color: z.string().refine((val) => isValidHexColor(val), {
    message: '有効なカラーコード（例: #3B82F6）を入力してください',
  }),
  secondary_color: z.string().refine((val) => isValidHexColor(val), {
    message: '有効なカラーコード（例: #1F2937）を入力してください',
  }),
  system_prompt: z.string().max(2000, 'システムプロンプトは2000文字以内で入力してください'),
  fallback_message: z.string().max(500, 'フォールバックメッセージは500文字以内で入力してください'),
  rate_limit: z.number().min(RATE_LIMIT.min).max(RATE_LIMIT.max),
  max_message_length: z.number().min(MESSAGE_LENGTH.min).max(MESSAGE_LENGTH.max),
  context_turns: z.number().min(CONTEXT_TURNS.min).max(CONTEXT_TURNS.max),
  suggested_questions: z.array(z.string()).max(5, '推奨質問は5つまでです'),
  crawl_schedule: z.enum(['off', 'daily', 'weekly', 'monthly']),
  lead_collection_enabled: z.boolean(),
  lead_trigger_after_replies: z.number().int().min(1).max(10),
});

type SettingsFormData = z.infer<typeof settingsSchema>;

interface DomainSettingsProps {
  domain: DomainDetail;
}

export const DomainSettings: React.FC<DomainSettingsProps> = ({ domain }) => {
  const { mutate: updateDomain, isPending } = useUpdateDomain(domain.id);
  const plan = useAuthStore((s) => s.user?.plan);
  const planRateLimitMax = (PLAN_INFO as Record<string, { rate_limit_per_min?: number }>)[plan ?? 'trial']?.rate_limit_per_min ?? RATE_LIMIT.max;
  const [newQuestion, setNewQuestion] = React.useState('');
  const [previewTab, setPreviewTab] = useState('settings');
  const [pendingTemplateId, setPendingTemplateId] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 899px)');
    setIsMobile(mq.matches);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    getValues,
    formState: { errors, isDirty },
    reset,
  } = useForm<SettingsFormData>({
    resolver: zodResolver(settingsSchema),
    defaultValues: {
      description: domain.description || '',
      bot_name: domain.settings.bot_name || DEFAULT_BOT_SETTINGS.bot_name,
      welcome_message: domain.settings.welcome_message || DEFAULT_BOT_SETTINGS.welcome_message,
      placeholder: domain.settings.placeholder || DEFAULT_BOT_SETTINGS.placeholder,
      primary_color: domain.settings.primary_color || DEFAULT_BOT_SETTINGS.primary_color,
      secondary_color: domain.settings.secondary_color || DEFAULT_BOT_SETTINGS.secondary_color,
      system_prompt: domain.settings.system_prompt || DEFAULT_BOT_SETTINGS.system_prompt,
      fallback_message: domain.settings.fallback_message || '現在回答を準備中です。しばらくお待ちいただくか、お問い合わせフォームからご連絡ください。',
      rate_limit: domain.settings.rate_limit || DEFAULT_BOT_SETTINGS.rate_limit,
      max_message_length: domain.settings.max_message_length || DEFAULT_BOT_SETTINGS.max_message_length,
      context_turns: domain.settings.context_turns || DEFAULT_BOT_SETTINGS.context_turns,
      suggested_questions: domain.settings.suggested_questions || [],
      crawl_schedule: (domain.crawl_schedule || 'off') as 'off' | 'daily' | 'weekly' | 'monthly',
      lead_collection_enabled: domain.settings.lead_collection_enabled === true,
      lead_trigger_after_replies: domain.settings.lead_trigger_after_replies ?? 3,
    },
  });

  useEffect(() => {
    reset({
      description: domain.description || '',
      bot_name: domain.settings.bot_name || DEFAULT_BOT_SETTINGS.bot_name,
      welcome_message: domain.settings.welcome_message || DEFAULT_BOT_SETTINGS.welcome_message,
      placeholder: domain.settings.placeholder || DEFAULT_BOT_SETTINGS.placeholder,
      primary_color: domain.settings.primary_color || DEFAULT_BOT_SETTINGS.primary_color,
      secondary_color: domain.settings.secondary_color || DEFAULT_BOT_SETTINGS.secondary_color,
      system_prompt: domain.settings.system_prompt || DEFAULT_BOT_SETTINGS.system_prompt,
      fallback_message: domain.settings.fallback_message || '現在回答を準備中です。しばらくお待ちいただくか、お問い合わせフォームからご連絡ください。',
      rate_limit: domain.settings.rate_limit || DEFAULT_BOT_SETTINGS.rate_limit,
      max_message_length: domain.settings.max_message_length || DEFAULT_BOT_SETTINGS.max_message_length,
      context_turns: domain.settings.context_turns || DEFAULT_BOT_SETTINGS.context_turns,
      suggested_questions: domain.settings.suggested_questions || [],
      crawl_schedule: domain.crawl_schedule || 'off',
      lead_collection_enabled: domain.settings.lead_collection_enabled ?? false,
      lead_trigger_after_replies: domain.settings.lead_trigger_after_replies ?? 3,
    });
  }, [domain, reset]);

  const watchedValues = watch();
  const suggestedQuestions = watchedValues.suggested_questions || [];
  const [previewData, setPreviewData] = useState<SettingsFormData>(getValues());
  const { showModal: showGuardModal, onDiscard: onGuardDiscard, onContinue: onGuardContinue } = useFormGuard(isDirty);

  useEffect(() => {
    const timer = setTimeout(() => {
      setPreviewData(watchedValues);
    }, 300);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(watchedValues)]);

  const formatCrawlDate = (dateStr: string | null | undefined): string => {
    if (!dateStr) return '未実行';
    try {
      return new Date(dateStr).toLocaleString('ja-JP', {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit',
      });
    } catch {
      return '未実行';
    }
  };

  const onSubmit = (data: SettingsFormData) => {
    updateDomain({
      description: data.description,
      crawl_schedule: data.crawl_schedule,
      settings: {
        bot_name: data.bot_name,
        welcome_message: data.welcome_message,
        placeholder: data.placeholder,
        primary_color: data.primary_color,
        secondary_color: data.secondary_color,
        system_prompt: data.system_prompt,
        fallback_message: data.fallback_message,
        rate_limit: data.rate_limit,
        max_message_length: data.max_message_length,
        context_turns: data.context_turns,
        suggested_questions: data.suggested_questions,
        lead_collection_enabled: data.lead_collection_enabled,
        lead_trigger_after_replies: data.lead_trigger_after_replies,
      },
    });
  };

  const handleAddQuestion = () => {
    if (newQuestion.trim() && suggestedQuestions.length < 5) {
      setValue('suggested_questions', [...suggestedQuestions, newQuestion.trim()], { shouldDirty: true });
      setNewQuestion('');
    }
  };

  const handleRemoveQuestion = (index: number) => {
    setValue(
      'suggested_questions',
      suggestedQuestions.filter((_, i) => i !== index),
      { shouldDirty: true }
    );
  };

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
        {isMobile && (
          <div className="md:col-span-12">
            <Tabs value={previewTab} onValueChange={setPreviewTab} data-testid="settings-preview-tabs">
              <TabsList>
                <TabsTrigger value="settings">設定</TabsTrigger>
                <TabsTrigger value="preview">プレビュー</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        )}

        {/* 設定フォームパネル */}
        <div
          className={`md:col-span-7 ${isMobile && previewTab !== 'settings' ? 'hidden' : 'block'}`}
          data-testid="settings-form-panel"
        >
          <form onSubmit={handleSubmit(onSubmit)}>
            {/* 常時表示セクション */}
            <div data-testid="settings-primary-section">
              {/* BOT名 */}
              <div className="bg-white rounded-lg shadow-sm border mb-4" data-testid="bot_name">
                <div className="px-6 pt-5 pb-2">
                  <h3 className="text-sm font-semibold">BOT名</h3>
                </div>
                <div className="px-6 pb-5">
                  <Controller
                    name="bot_name"
                    control={control}
                    render={({ field }) => (
                      <div>
                        <Input {...field} className="w-full" />
                        {errors.bot_name && (
                          <p className="text-sm text-red-500 mt-1">{errors.bot_name.message}</p>
                        )}
                      </div>
                    )}
                  />
                </div>
              </div>

              {/* テーマカラー */}
              <div className="bg-white rounded-lg shadow-sm border mb-4">
                <div className="px-6 pt-5 pb-2">
                  <h3 className="text-sm font-semibold">テーマカラー</h3>
                </div>
                <div className="px-6 pb-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Controller
                    name="primary_color"
                    control={control}
                    render={({ field }) => (
                      <div data-testid="primary_color">
                        <label className="block text-sm font-medium mb-1.5">テーマカラー1</label>
                        <div className="flex items-center gap-2">
                          <input
                            type="color"
                            value={field.value || '#3B82F6'}
                            onChange={(e) => field.onChange(e.target.value)}
                            data-testid="color-picker-primary"
                            className="w-9 h-9 p-0 border-0 cursor-pointer bg-transparent rounded"
                            style={{ width: 36, height: 36 }}
                          />
                          <Input {...field} placeholder="#3B82F6" className="flex-1" />
                        </div>
                        {errors.primary_color && (
                          <p className="text-sm text-red-500 mt-1">{errors.primary_color.message}</p>
                        )}
                      </div>
                    )}
                  />
                  <Controller
                    name="secondary_color"
                    control={control}
                    render={({ field }) => (
                      <div data-testid="secondary_color">
                        <label className="block text-sm font-medium mb-1.5">テーマカラー2</label>
                        <div className="flex items-center gap-2">
                          <input
                            type="color"
                            value={field.value || '#1F2937'}
                            onChange={(e) => field.onChange(e.target.value)}
                            data-testid="color-picker-secondary"
                            className="w-9 h-9 p-0 border-0 cursor-pointer bg-transparent rounded"
                            style={{ width: 36, height: 36 }}
                          />
                          <Input {...field} placeholder="#1F2937" className="flex-1" />
                        </div>
                        {errors.secondary_color && (
                          <p className="text-sm text-red-500 mt-1">{errors.secondary_color.message}</p>
                        )}
                      </div>
                    )}
                  />
                </div>
              </div>

              {/* 初期メッセージ */}
              <div className="bg-white rounded-lg shadow-sm border mb-4" data-testid="welcome_message">
                <div className="px-6 pt-5 pb-2">
                  <h3 className="text-sm font-semibold">初期メッセージ</h3>
                </div>
                <div className="px-6 pb-5">
                  <Controller
                    name="welcome_message"
                    control={control}
                    render={({ field }) => (
                      <div>
                        <textarea
                          {...field}
                          rows={2}
                          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                        />
                        {errors.welcome_message && (
                          <p className="text-sm text-red-500 mt-1">{errors.welcome_message.message}</p>
                        )}
                      </div>
                    )}
                  />
                </div>
              </div>

              {/* おすすめ質問 */}
              <div className="bg-white rounded-lg shadow-sm border mb-4" data-testid="suggested_questions">
                <div className="px-6 pt-5 pb-2">
                  <h3 className="text-sm font-semibold">おすすめ質問（最大5つ）</h3>
                </div>
                <div className="px-6 pb-5">
                  <div className="flex flex-wrap gap-2 mb-3">
                    {suggestedQuestions.map((q, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm bg-gray-100"
                      >
                        {q}
                        <button
                          type="button"
                          onClick={() => handleRemoveQuestion(index)}
                          className="ml-1 text-gray-400 hover:text-gray-600"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                  {suggestedQuestions.length < 5 && (
                    <div className="flex gap-2">
                      <Input
                        value={newQuestion}
                        onChange={(e) => setNewQuestion(e.target.value)}
                        placeholder="おすすめ質問を追加..."
                        className="flex-1"
                        data-testid="suggested-questions-input"
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleAddQuestion();
                          }
                        }}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleAddQuestion}
                        disabled={!newQuestion.trim()}
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        追加
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              {/* リード収集 */}
              <div className="bg-white rounded-lg shadow-sm border mb-4" data-testid="lead_collection_enabled">
                <div className="px-6 pt-5 pb-2">
                  <h3 className="text-sm font-semibold">リード収集</h3>
                </div>
                <div className="px-6 pb-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">リード収集を有効にする</p>
                      <p className="text-xs text-text-secondary mt-0.5">
                        訪問者の名前・メールアドレスを収集します
                      </p>
                    </div>
                    <Controller
                      name="lead_collection_enabled"
                      control={control}
                      render={({ field }) => (
                        <button
                          type="button"
                          role="switch"
                          aria-checked={field.value}
                          data-testid="lead-collection-toggle"
                          onClick={() => field.onChange(!field.value)}
                          className={[
                            'relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent',
                            'transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2',
                            field.value ? 'bg-primary' : 'bg-gray-200',
                            'cursor-pointer',
                          ].join(' ')}
                        >
                          <span
                            aria-hidden="true"
                            className={[
                              'pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow',
                              'transform transition duration-200 ease-in-out',
                              field.value ? 'translate-x-5' : 'translate-x-0',
                            ].join(' ')}
                          />
                        </button>
                      )}
                    />
                  </div>
                  <div className="mt-4" data-testid="lead_trigger_after_replies">
                    <label className="block text-sm font-medium mb-1.5">
                      後出しトリガー（N回返答後に表示・既定3）
                    </label>
                    <Controller
                      name="lead_trigger_after_replies"
                      control={control}
                      render={({ field }) => (
                        <input
                          type="number"
                          min={1}
                          max={10}
                          data-testid="lead-trigger-after-replies-input"
                          value={field.value}
                          onChange={(e) => field.onChange(Number(e.target.value))}
                          className="w-24 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                        />
                      )}
                    />
                    <p className="text-xs text-text-secondary mt-1">
                      ボットがN回返答した後、会話を中断せずフォームをインライン表示します（1〜10）
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* 詳細設定トグル */}
            <button
              type="button"
              data-testid="settings-advanced-toggle"
              onClick={() => setIsAdvancedOpen((prev) => !prev)}
              className="w-full flex items-center justify-between px-4 py-3 mb-4 bg-white rounded-lg shadow-sm border text-sm font-semibold hover:bg-gray-50 transition-colors"
            >
              <span>詳細設定</span>
              <ChevronDown
                className={`h-4 w-4 text-gray-500 transition-transform duration-300 ${isAdvancedOpen ? 'rotate-180' : ''}`}
              />
            </button>

            {/* 詳細設定セクション（折りたたみ） */}
            <div
              data-testid="settings-advanced-section"
              style={{
                overflow: 'hidden',
                maxHeight: isAdvancedOpen ? '9999px' : '0',
                transition: 'max-height 0.4s ease',
              }}
            >
              {/* システムプロンプト */}
              <div className="bg-white rounded-lg shadow-sm border mb-4">
                <div className="px-6 pt-5 pb-2">
                  <h3 className="text-sm font-semibold">AI設定</h3>
                </div>
                <div className="px-6 pb-5">
                  <div className="mb-4" data-testid="template-selector">
                    <p className="text-sm font-medium mb-2">テンプレートから選択</p>
                    <div className="flex flex-wrap gap-2">
                      {SYSTEM_PROMPT_TEMPLATES.map((tpl) => (
                        <button
                          key={tpl.id}
                          type="button"
                          data-testid={`template-chip-${tpl.id}`}
                          onClick={() => {
                            const current = getValues('system_prompt');
                            const isDefault = current === '' || current === DEFAULT_BOT_SETTINGS.system_prompt;
                            if (isDefault) {
                              setValue('system_prompt', tpl.prompt, { shouldDirty: true });
                            } else {
                              setPendingTemplateId(tpl.id);
                            }
                          }}
                          className="inline-flex items-center px-3 py-1 rounded-full text-sm border border-input hover:bg-gray-50 transition-colors"
                        >
                          {tpl.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <Controller
                    name="system_prompt"
                    control={control}
                    render={({ field }) => (
                      <div className="mb-4">
                        <label className="block text-sm font-medium mb-1.5">システムプロンプト</label>
                        <textarea
                          {...field}
                          rows={6}
                          data-testid="system-prompt-input"
                          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                        />
                        {errors.system_prompt ? (
                          <p className="text-sm text-red-500 mt-1">{errors.system_prompt.message}</p>
                        ) : (
                          <p className="text-xs text-text-secondary mt-1">AIの応答の仕方を制御するプロンプト</p>
                        )}
                      </div>
                    )}
                  />

                  {/* model — 準備中 */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-1.5">LLMモデル選択 (model)</label>
                    <div className="rounded-md border border-input bg-gray-50 px-3 py-2 text-sm text-text-secondary">
                      準備中
                    </div>
                  </div>

                  <Controller
                    name="fallback_message"
                    control={control}
                    render={({ field }) => (
                      <div>
                        <label className="block text-sm font-medium mb-1.5">フォールバックメッセージ</label>
                        <textarea
                          {...field}
                          rows={3}
                          data-testid="fallback-message-input"
                          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                        />
                        {errors.fallback_message ? (
                          <p className="text-sm text-red-500 mt-1">{errors.fallback_message.message}</p>
                        ) : (
                          <p className="text-xs text-text-secondary mt-1">学習データに回答がない場合にユーザーに表示するメッセージ（500文字以内）</p>
                        )}
                      </div>
                    )}
                  />
                </div>
              </div>

              {/* 制限設定 */}
              <div className="bg-white rounded-lg shadow-sm border mb-4">
                <div className="px-6 pt-5 pb-2">
                  <h3 className="text-sm font-semibold">制限設定</h3>
                </div>
                <div className="px-6 pb-5 grid grid-cols-1 sm:grid-cols-3 gap-6">
                  <Controller
                    name="rate_limit"
                    control={control}
                    render={({ field }) => (
                      <div data-testid="rate_limit">
                        <label className="block text-sm font-medium mb-2">レート制限（回/分）</label>
                        <input
                          type="range"
                          min={RATE_LIMIT.min}
                          max={planRateLimitMax}
                          name={field.name}
                          ref={field.ref}
                          value={field.value}
                          onChange={(e) => field.onChange(Number(e.target.value))}
                          onBlur={field.onBlur}
                          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary"
                        />
                        <p className="text-xs text-text-secondary mt-1">現在: {field.value}回/分</p>
                      </div>
                    )}
                  />
                  <Controller
                    name="max_message_length"
                    control={control}
                    render={({ field }) => (
                      <div data-testid="max_message_length">
                        <label className="block text-sm font-medium mb-2">最大メッセージ長（文字）</label>
                        <input
                          type="range"
                          min={MESSAGE_LENGTH.min}
                          max={MESSAGE_LENGTH.max}
                          step={100}
                          name={field.name}
                          ref={field.ref}
                          value={field.value}
                          onChange={(e) => field.onChange(Number(e.target.value))}
                          onBlur={field.onBlur}
                          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary"
                        />
                        <p className="text-xs text-text-secondary mt-1">現在: {field.value}文字</p>
                      </div>
                    )}
                  />
                  <Controller
                    name="context_turns"
                    control={control}
                    render={({ field }) => (
                      <div data-testid="context_turns">
                        <label className="block text-sm font-medium mb-2">コンテキストターン数</label>
                        <input
                          type="range"
                          min={CONTEXT_TURNS.min}
                          max={CONTEXT_TURNS.max}
                          name={field.name}
                          ref={field.ref}
                          value={field.value}
                          onChange={(e) => field.onChange(Number(e.target.value))}
                          onBlur={field.onBlur}
                          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary"
                        />
                        <p className="text-xs text-text-secondary mt-1">現在: {field.value}ターン</p>
                      </div>
                    )}
                  />
                </div>
              </div>

              {/* 詳細オプション（render_mode, response_language, auto_display） */}
              <div className="bg-white rounded-lg shadow-sm border mb-4">
                <div className="px-6 pt-5 pb-2">
                  <h3 className="text-sm font-semibold">詳細オプション</h3>
                </div>
                <div className="px-6 pb-5 grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div data-testid="render_mode">
                    <label className="block text-sm font-medium mb-1.5">クロールレンダリングモード (render_mode)</label>
                    <div className="rounded-md border border-input bg-gray-50 px-3 py-2 text-sm text-text-secondary">
                      準備中
                    </div>
                  </div>
                  <div data-testid="response_language">
                    <label className="block text-sm font-medium mb-1.5">応答言語 (response_language)</label>
                    <div className="rounded-md border border-input bg-gray-50 px-3 py-2 text-sm text-text-secondary">
                      準備中
                    </div>
                  </div>
                  <div data-testid="auto_display">
                    <label className="block text-sm font-medium mb-1.5">自動表示設定 (auto_display)</label>
                    <div className="rounded-md border border-input bg-gray-50 px-3 py-2 text-sm text-text-secondary">
                      準備中
                    </div>
                  </div>
                </div>
              </div>

              {/* 基本設定（説明・プレースホルダー） */}
              <div className="bg-white rounded-lg shadow-sm border mb-4">
                <div className="px-6 pt-5 pb-2">
                  <h3 className="text-sm font-semibold">基本設定</h3>
                </div>
                <div className="px-6 pb-5 grid grid-cols-1 gap-4">
                  <Controller
                    name="description"
                    control={control}
                    render={({ field }) => (
                      <div>
                        <label className="block text-sm font-medium mb-1.5">説明</label>
                        <textarea
                          {...field}
                          rows={2}
                          placeholder="このドメインの用途など"
                          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                        />
                        {errors.description && (
                          <p className="text-sm text-red-500 mt-1">{errors.description.message}</p>
                        )}
                      </div>
                    )}
                  />
                  <Controller
                    name="placeholder"
                    control={control}
                    render={({ field }) => (
                      <div>
                        <label className="block text-sm font-medium mb-1.5">入力欄プレースホルダー</label>
                        <Input {...field} className="w-full" data-testid="placeholder-input" />
                        {errors.placeholder && (
                          <p className="text-sm text-red-500 mt-1">{errors.placeholder.message}</p>
                        )}
                      </div>
                    )}
                  />
                </div>
              </div>

              {/* クロールスケジュール */}
              <div className="bg-white rounded-lg shadow-sm border mb-4">
                <div className="px-6 pt-5 pb-2">
                  <h3 className="text-sm font-semibold">クロールスケジュール</h3>
                </div>
                <div className="px-6 pb-5">
                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-1.5">自動再クロール頻度</label>
                    <Controller
                      name="crawl_schedule"
                      control={control}
                      render={({ field }) => (
                        <Select value={field.value} onValueChange={field.onChange}>
                          <SelectTrigger data-testid="crawl-schedule-select">
                            <SelectValue placeholder="選択してください" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="off">オフ</SelectItem>
                            <SelectItem value="daily">毎日</SelectItem>
                            <SelectItem value="weekly">毎週</SelectItem>
                            <SelectItem value="monthly">毎月</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-text-secondary mb-1">最終クロール日時</p>
                      <p data-testid="crawl-last-crawled-at" className="font-medium">
                        {formatCrawlDate(domain.last_crawled_at)}
                      </p>
                    </div>
                    <div>
                      <p className="text-text-secondary mb-1">次回クロール予定</p>
                      <p data-testid="crawl-next-crawl-at" className="font-medium">
                        {formatCrawlDate(domain.next_crawl_at)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 保存ボタン */}
            <div className="flex justify-end gap-3 mt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => reset()}
                disabled={!isDirty || isPending}
              >
                リセット
              </Button>
              <LoadingButton type="submit" loading={isPending} disabled={!isDirty}>変更を保存</LoadingButton>
            </div>
          </form>
        </div>

        {/* プレビューパネル */}
        <div
          className={`md:col-span-5 ${isMobile && previewTab !== 'preview' ? 'hidden' : 'block'}`}
          data-testid="preview-panel"
        >
          <PreviewWidget
            botName={previewData.bot_name || ''}
            welcomeMessage={previewData.welcome_message || ''}
            primaryColor={previewData.primary_color || '#4A90D9'}
            secondaryColor={previewData.secondary_color || '#1a237e'}
            placeholder={previewData.placeholder || ''}
            suggestedQuestions={previewData.suggested_questions || []}
          />
        </div>
      </div>

      {/* テンプレート上書き確認モーダル */}
      {pendingTemplateId && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center"
          data-testid="template-overwrite-dialog"
        >
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md mx-4">
            <h3 className="text-lg font-semibold mb-2">現在のプロンプトを上書きしますか？</h3>
            <p className="text-sm text-text-secondary mb-4">
              現在入力されているシステムプロンプトは失われます。テンプレートの内容で上書きしてよろしいですか？
            </p>
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setPendingTemplateId(null)}
                data-testid="template-overwrite-cancel"
              >
                キャンセル
              </Button>
              <Button
                type="button"
                onClick={() => {
                  const tpl = SYSTEM_PROMPT_TEMPLATES.find((t) => t.id === pendingTemplateId);
                  if (tpl) {
                    setValue('system_prompt', tpl.prompt, { shouldDirty: true });
                  }
                  setPendingTemplateId(null);
                }}
                data-testid="template-overwrite-confirm"
              >
                上書きする
              </Button>
            </div>
          </div>
        </div>
      )}

      <UnsavedChangesModal isOpen={showGuardModal} onDiscard={onGuardDiscard} onContinue={onGuardContinue} />
    </>
  );
};
